import type { PublicPricing } from '../services/api';

export function validatedPublicPricing(value: PublicPricing): PublicPricing {
  const saudi = value.currency === 'SAR' && value.monthlyAmount === 39 && value.annualAmount === 300;
  const international = value.currency === 'USD' && value.monthlyAmount === 12 && value.annualAmount === 99;
  if ((!saudi && !international) || value.promo?.eligible !== false || value.isSaudiArabia !== saudi) {
    throw new Error('Billing prices are unavailable');
  }
  return value;
}

/** All mounted pricing surfaces share availability and expire stale prices together. */
export function createPricingResource(load: () => Promise<PublicPricing>, now = Date.now, ttlMs = 60_000) {
  const loadingState: { pricing: PublicPricing | null; error: Error | null; loading: boolean } = { pricing: null, error: null, loading: true };
  let state = loadingState;
  let expiresAt = 0;
  let inflight: Promise<void> | null = null;
  const listeners = new Set<() => void>();
  const publish = (next: typeof state) => { state = next; listeners.forEach(listener => listener()); };
  return {
    snapshot: () => state.pricing && now() >= expiresAt ? loadingState : state,
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    refresh(force = false): Promise<void> {
      if (inflight) return inflight;
      if (!force && state.pricing && now() < expiresAt) return Promise.resolve();
      publish({ pricing: null, error: null, loading: true });
      inflight = Promise.resolve().then(load).then(validatedPublicPricing).then(pricing => {
        expiresAt = now() + ttlMs;
        publish({ pricing, error: null, loading: false });
      }).catch((error: unknown) => {
        expiresAt = 0;
        publish({ pricing: null, error: error instanceof Error ? error : new Error('Billing prices are unavailable'), loading: false });
      }).finally(() => { inflight = null; });
      return inflight;
    },
  };
}
